import { getDeveloperApi } from '@squareup/dex-data-shared-developer-api';
import SandboxRenewAuthDialog from '@squareup/dex-feature-dex-tech-main-sandbox-authorization-renewal-dialog';
import { useRbacActionGuard } from '@squareup/dex-feature-shared-main-rbac-permission-check';
import { useAccessTokens } from '@squareup/dex-store-access-shared-main-use-access-tokens-hook';
import { ApplicationType } from '@squareup/dex-types-shared-application-type';
import { PermissionSetDomainAction } from '@squareup/dex-types-shared-rbac-permissions';
import {
  useApiExplorerAccessTokenData,
  useApiExplorerContext,
} from '@squareup/dex-ui-shared-api-explorer-providers';
import { useSearchParamsObject } from '@squareup/dex-utils-shared-routing-hooks';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { AccessTokenField } from './AccessTokenField';
import { ExpiredTokenMessage } from './ExpiredTokenWarning';
import { useExpiredAuth, useSupportedEnvironment } from './access-token-hooks';

type Props = {
  supportedEnvironments?: ApplicationType[];
};

const AccessTokenFieldContainer: React.FC<Props> = ({
  supportedEnvironments = [ApplicationType.Sandbox, ApplicationType.Production],
}) => {
  const { appId } = useSearchParamsObject();

  const [showRenewAuthDialog, setShowRenewAuthDialog] = useState(false);

  const { dispatchAccessTokenAction } = useApiExplorerContext();
  const { token: accessToken, productionClient } =
    useApiExplorerAccessTokenData();

  const { data: userInfo, isLoading: isLoadingUserInfo } =
    getDeveloperApi().useGetUserInfoQuery();
  const {
    data: accessTokens,
    isLoading: isLoadingAccessTokens,
    errors: accessTokensErrors,
  } = useAccessTokens({
    skip: !userInfo?.person,
  });
  const isAuthenticated = Boolean(userInfo?.person);
  const isMerchantSelected = Boolean(userInfo?.merchant);
  const isLoading = isLoadingUserInfo || isLoadingAccessTokens;

  const { environment } = useSupportedEnvironment(
    isLoading,
    supportedEnvironments,
    accessTokens,
    appId
  );

  const accessTokensForEnvironment = useMemo(
    () => accessTokens?.[environment],
    [accessTokens, environment]
  );

  const { expiredAuth, setExpiredAuth } = useExpiredAuth(
    accessToken,
    accessTokensForEnvironment,
    isAuthenticated
  );

  const onShowRenewAuthDialog = useCallback(
    (state: boolean) => () => setShowRenewAuthDialog(state),
    []
  );
  const clearExpiredAuth = useCallback(
    () => setExpiredAuth(undefined),
    [setExpiredAuth]
  );

  const {
    action: showRenewAuthDialogOrPermissionsError,
    isLoading: isTokenPermissionsCheckLoading,
  } = useRbacActionGuard({
    action: onShowRenewAuthDialog(true),
    actionName: PermissionSetDomainAction.UPDATE_SANDBOX_ENVIRONMENT_DETAILS,
    skip: !isAuthenticated || !isMerchantSelected,
  });

  useEffect(() => {
    // Initializes the `productionClient` value if it is not set in the API Explorer context
    // This happens on page load when the user has a stored access token and on environment change
    if (!isLoading && !productionClient && accessToken) {
      let productionClientId = '';
      if (environment === ApplicationType.Production) {
        // Production access tokens have the client_id in the access token object
        productionClientId =
          accessTokens?.[ApplicationType.Production]?.accessTokens.find(
            (token) => token.token === accessToken
          )?.client_id || '';
      }
      if (environment === ApplicationType.Sandbox) {
        // Sandbox access tokens have the client_id in the access token object
        // or in the authorization object (for primary merchant/default accounts)
        const sandboxClientId =
          accessTokens?.[ApplicationType.Sandbox]?.accessTokens.find(
            (token) => token.token === accessToken
          )?.client_id ||
          accessTokens?.[ApplicationType.Sandbox]?.authorizations.find(
            (auth) => auth.access_token === accessToken
          )?.client ||
          '';
        productionClientId =
          accessTokens?.[ApplicationType.Sandbox]?.oauthClients.find(
            (client) => client.id === sandboxClientId
          )?.production_client || '';
      }

      if (productionClientId) {
        dispatchAccessTokenAction({
          type: 'SET_PRODUCTION_CLIENT',
          payload: productionClientId,
        });
      }
    }
  }, [
    accessToken,
    accessTokens,
    productionClient,
    dispatchAccessTokenAction,
    environment,
    isLoading,
  ]);

  return (
    <>
      <AccessTokenField
        accessTokens={accessTokensForEnvironment}
        environment={environment}
        errors={accessTokensErrors}
        isAuthenticated={isAuthenticated}
        isLoading={isLoading}
        supportedEnvironments={supportedEnvironments}
        value={accessToken}
      />
      {Boolean(expiredAuth) && (
        <ExpiredTokenMessage
          isLoading={isTokenPermissionsCheckLoading}
          onShowDialog={showRenewAuthDialogOrPermissionsError}
        />
      )}
      {showRenewAuthDialog && expiredAuth && (
        <SandboxRenewAuthDialog
          authorization={expiredAuth.auth}
          onClose={onShowRenewAuthDialog(false)}
          onRenew={clearExpiredAuth}
          merchantName={expiredAuth.name}
        />
      )}
    </>
  );
};

export default AccessTokenFieldContainer;
